
// providers/FormProvider.tsx - Global Form Provider
import React, { createContext, useContext, useEffect } from 'react';
import { FormProvider as RHFFormProvider, useForm } from 'react-hook-form';
import { useValuationFormStore, ValuationFormValues } from '../store/valuationFormStore';

interface GlobalFormContextType {
  // Add any additional global form methods here
  syncFormWithStore: () => void;
  resetGlobalForm: () => void;
  isFormDirty: boolean;
}

const GlobalFormContext = createContext<GlobalFormContextType | null>(null);

interface GlobalFormProviderProps {
  children: React.ReactNode;
}

export const GlobalFormProvider: React.FC<GlobalFormProviderProps> = ({ children }) => {
  const { formData, updateFormData, resetForm } = useValuationFormStore();

  const methods = useForm<ValuationFormValues>({
    defaultValues: formData,
    mode: 'onChange'
  });

  const { reset, getValues, formState: { isDirty } } = methods;

  // Sync form with Zustand store when formData changes
  useEffect(() => {
    reset(formData);
  }, [formData, reset]);

  // Auto-save form data to store on every change (optional)
//   const watchedValues = methods.watch();
//   useEffect(() => {
   
//     const timeoutId = setTimeout(() => {
//       updateFormData(watchedValues);
//     }, 300); 

//     return () => clearTimeout(timeoutId);
//   }, [watchedValues, updateFormData]);

  const syncFormWithStore = () => {
    const currentFormData = getValues();
    updateFormData(currentFormData);
  };

  const resetGlobalForm = () => {
    resetForm();
    reset();
  };

  const contextValue: GlobalFormContextType = {
    syncFormWithStore,
    resetGlobalForm,
    isFormDirty: isDirty
  };

  return (
    <RHFFormProvider {...methods}>
      <GlobalFormContext.Provider value={contextValue}>
        {children}
      </GlobalFormContext.Provider>
    </RHFFormProvider>
  );
};

// Hook to access global form context
export const useGlobalFormContext = () => {
  const context = useContext(GlobalFormContext);
  if (!context) {
    throw new Error('useGlobalFormContext must be used within GlobalFormProvider');
  }
  return context;
};